import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  compareSerenityConfigs,
  ConfigChange,
  ConfigComparison,
  formatConfigValue,
  groupChangesByModule,
} from "../../../api/serenityConfig";
import { SerenityConfigSummary } from "../../../generated/types";
import { Modal } from "../../common/Modal";
import { Select } from "../../common/Select";

type CompareState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; comparison: ConfigComparison };

const formatDate = (date: string) => new Date(date).toLocaleDateString();

/** Valeur simple en clair ; objet/liste abrégé, déplié au clic en JSON indenté. */
const ConfigValue = ({ value }: { value: unknown }) =>
  value !== null && typeof value === "object" ? (
    <details className="min-w-0 max-w-full">
      <summary className="cursor-pointer">{formatConfigValue(value)}</summary>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all text-xs bg-grey-light/20 rounded p-2 mt-1">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  ) : (
    <span>{formatConfigValue(value)}</span>
  );

const ChangeLine = ({ change }: { change: ConfigChange }) => {
  const { t } = useTranslation();
  const label = change.path.join(" › ") || change.module;
  if (change.kind === "renamed") {
    return (
      <li className="text-sm font-oxaniumMedium">
        {change.path.length > 0 && `${label} › `}
        {t("serenity-configs-change-renamed", {
          before: formatConfigValue(change.before),
          after: formatConfigValue(change.after),
        })}
      </li>
    );
  }
  return (
    <li className="flex flex-wrap gap-x-2 text-sm">
      <span className="font-oxaniumMedium">{label}</span>
      {change.kind === "added" && (
        <span className="text-secondary">
          {t("serenity-configs-change-added")}
        </span>
      )}
      {change.kind === "removed" && (
        <span className="text-warning-readable">
          {t("serenity-configs-change-removed")}
        </span>
      )}
      <span className="flex flex-wrap gap-x-2 min-w-0 max-w-full text-grey-dark">
        {change.kind !== "added" && <ConfigValue value={change.before} />}
        {change.kind === "changed" && <span>→</span>}
        {change.kind !== "removed" && <ConfigValue value={change.after} />}
      </span>
    </li>
  );
};

/** Compare deux sauvegardes de la box ; par défaut l'avant-dernière et la dernière. */
export const SerenityConfigCompareModal = ({
  uuid,
  configs,
  isOpen,
  onClose,
}: {
  uuid: string;
  /** La plus récente d'abord, au moins deux. */
  configs: SerenityConfigSummary[];
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [fromId, setFromId] = useState(configs[1].id);
  const [toId, setToId] = useState(configs[0].id);
  const [state, setState] = useState<CompareState>({ status: "loading" });

  useEffect(() => {
    if (!isOpen || fromId === toId) return;
    let cancelled = false;
    setState({ status: "loading" });
    compareSerenityConfigs(uuid, fromId, toId)
      .then((comparison) => {
        if (!cancelled) setState({ status: "ready", comparison });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [uuid, fromId, toId, isOpen]);

  // Le libellé n'est lu que par les lecteurs d'écran : la flèche entre les listes dit le sens.
  const picker = (
    value: number,
    onChange: (id: number) => void,
    label: string,
  ) => (
    <Select
      aria-label={label}
      size="compact"
      className="flex-1 min-w-0"
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
    >
      {configs.map((config) => (
        <option key={config.id} value={config.id}>
          {t("serenity-configs-download", {
            date: formatDate(config.date),
            version: config.version,
          })}
        </option>
      ))}
    </Select>
  );

  const result = () => {
    if (fromId === toId) {
      return <p className="text-sm">{t("serenity-configs-compare-same")}</p>;
    }
    if (state.status === "loading") return <p className="text-sm">…</p>;
    if (state.status === "error") {
      return <p className="text-sm">{t("serenity-configs-compare-error")}</p>;
    }
    const { from, to, changes } = state.comparison;
    return (
      <div className="space-y-4">
        {from.version !== to.version && (
          <p className="text-sm">
            {t("serenity-configs-compare-version", {
              before: from.version,
              after: to.version,
            })}
          </p>
        )}
        {changes.length === 0 ? (
          <p className="text-sm">{t("serenity-configs-compare-none")}</p>
        ) : (
          <>
            {groupChangesByModule(changes).map((group) => (
              <section key={group.module}>
                <h4 className="font-oxaniumMedium text-tertiary">
                  {group.module}
                </h4>
                <ul className="space-y-1 border-l-2 border-secondary pl-3 ml-1">
                  {group.changes.map((change) => (
                    <ChangeLine
                      key={`${change.path.join("/")}-${change.kind}`}
                      change={change}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("serenity-configs-compare-title")}
      maxWidth="2xl"
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          {picker(fromId, setFromId, t("serenity-configs-compare-from"))}
          <FontAwesomeIcon
            icon="arrow-right"
            className="shrink-0 text-secondary"
          />
          {picker(toId, setToId, t("serenity-configs-compare-to"))}
        </div>
        {result()}
      </div>
    </Modal>
  );
};
